import type { PartiallyOptional } from '@cdc-internal/fe-toolbox/type-utils'
import { Box, Skeleton } from '@mantine/core'
import NextImage, { type ImageProps as NextImageProps } from 'next/image'
import { useState } from 'react'

const defaultPlaceholder =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mM0sgl6CgADDwGnZLteDwAAAABJRU5ErkJggg=='

export interface NextBaseImageProps
  extends Omit<PartiallyOptional<NextImageProps, 'src'>, 'width' | 'height'> {
  // Indicate loading state if src is not ready yet
  sourcePending?: boolean
  // Width of the image container
  width: string | number
  // Height of the image container
  height: string | number
  // Border radius
  radius?: string | number
  // Fallback content when image fails to load or src is not available
  fallback?: React.ReactNode
  // Background color for fallback
  fallbackBg?: string
}

export default function NextBaseImage({
  sourcePending,
  src,
  width,
  height,
  radius,
  fallback,
  fallbackBg = 'var(--bg-app)',
  ...restProps
}: NextBaseImageProps) {
  const [loadFailed, setLoadFailed] = useState(false)

  if (!src && sourcePending) {
    return (
      <Skeleton
        width={width}
        height={height}
        style={radius ? { borderRadius: radius } : undefined}
      />
    )
  }

  if (!src || loadFailed) {
    if (fallback) return fallback

    return (
      <Box
        w={width}
        h={height}
        style={{
          backgroundColor: fallbackBg,
          borderRadius: radius,
          flexShrink: 0,
          minWidth: 0,
          minHeight: 0,
        }}
      />
    )
  }

  return (
    <Box
      w={width}
      h={height}
      pos="relative"
      style={{
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: radius,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <NextImage
        src={src}
        fill
        style={{
          objectFit: 'cover',
        }}
        placeholder={defaultPlaceholder}
        onError={() => setLoadFailed(true)}
        {...restProps}
      />
    </Box>
  )
}
